import { useContext, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import ADND from '@aytm/lib/adnd/ADND'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import { DataContext } from '@aytm/app/stats/page/sidebar/coding/CodingPanel'

import { encodeText } from '@aytm/app/stats/lib/util/helpers'

import { TAG_MIN_LENGTH, TAG_MAX_LENGTH } from '@aytm/app/stats/page/components/lib/constants'
import { FORBIDDEN_TAG_SYMBOLS } from '@aytm/app/stats/page/sidebar/coding/lib/constants'

import useSingleAndDoubleClick from '@aytm/app/stats/page/components/hooks/useSingleAndDoubleClick'

import InputUpdate from '@aytm/app/stats/page/sidebar/coding/components/InputUpdate'
import TagSettings from '@aytm/app/stats/page/sidebar/coding/components/TagSettings'

import style from '@aytm/app/stats/page/sidebar/coding/styles/CodingPanel.module.scss'

const CodingTag = ({
  tag,
  error,
  settingsItem,
  isEditing,
  isProcessing,
  isCurrentProcessing,
  isNewProcessing,
  onDNDStart,
  onDNDEnd,
  setDndDroppedTag
}) => {
  const { root, mode, features, tagsList, onError, onTagConfirm, onCloseDropdown, onCancelEditing } =
    useContext(DataContext)

  const dndItemRef = useRef(null)

  const inputValueRef = useRef(tag.label)

  const isOpened = useMemo(
    () =>
      (settingsItem &&
        settingsItem.item == tag.tag &&
        settingsItem.type == tag.type &&
        settingsItem.codebook === tag.codebook) ||
      false,
    [tag, settingsItem]
  )

  const fetchData = useCallback(() => {
    return { ...tag, windowUID: PageGlobals.WINDOW_UID }
  }, [tag])

  const onActionConfirm = useCallback(
    action => {
      const _inputValue = inputValueRef.current.trim()
      const newValue = 'update' == action ? `${tag.codebook}: ${encodeText(_inputValue, false)}` : null

      if ('update' == action && newValue == tag.tag) {
        onCloseDropdown()
        onCancelEditing()
      } else if ('update' == action && (_inputValue.length < TAG_MIN_LENGTH || _inputValue.length > TAG_MAX_LENGTH)) {
        onError({
          codebook: tag.codebook,
          tag: tag.tag,
          name: 'updateError',
          message: `A code length should be between ${TAG_MIN_LENGTH} and ${TAG_MAX_LENGTH} characters`
        })
      } else if ('update' == action && tagsList.some(tag => newValue.toLowerCase() == tag.tag.toLowerCase())) {
        onCloseDropdown()
        onCancelEditing()

        setDndDroppedTag({ ...tag, update: _inputValue })
      } else {
        onTagConfirm(
          action,
          {
            ...tag,
            destination:
              'update' == action
                ? {
                    codebook: tag.codebook,
                    type: tag.type
                  }
                : null
          },
          encodeText(_inputValue, false)
        )
      }
    },
    [tag, tagsList, onError, onTagConfirm, onCloseDropdown, onCancelEditing, setDndDroppedTag]
  )

  const dndItem =
    isEditing || isProcessing || features.oe_tags_management.disabled
      ? null
      : ADND.sources
          .data({
            type: 'sidebar-tag',
            clearDragImage: false
          })
          .onStart(onDNDStart)
          .onEnd(onDNDEnd)
          .fetchData(fetchData)

  const addCommentsSearchOption = useCallback(
    () => dispatchEvent(new CustomEvent('addCommentsSearchOption', { detail: { item: tag } })),
    [tag]
  )

  const onTitleClick = useSingleAndDoubleClick(null, isProcessing ? null : addCommentsSearchOption)

  useEffect(() => {
    if (dndItem) dndItem.connect(dndItemRef.current)

    return () => {
      if (dndItem) dndItem.disconnect()
    }
  }, [dndItem])

  useEffect(() => isNewProcessing && dndItemRef.current.scrollIntoView(false), [isNewProcessing])

  return (
    <>
      <div
        className={classNames(style['codebook-item'], {
          [style['codebook-item-disabled']]: isNewProcessing,
          [style['blink']]: isNewProcessing
        })}
        type={tag.type}
      >
        <div
          ref={dndItemRef}
          data-testid={`stat-coding-tag-title-${tag.tag}`}
          className={classNames(style['codebook-subitem'], {
            [style['draggable']]: !isEditing && !isProcessing && !features.oe_tags_management.disabled
          })}
          onClick={['comments', 'video-responses'].includes(mode) ? onTitleClick : null}
        >
          <span className={style['codebook-item-icon']}>
            <i className="icon-aytmfd-tag" style={{ color: tag.color }} />
          </span>
          <div className={style['codebook-item-tag']}>
            <InputUpdate
              ariaLabel="Code name"
              root={root}
              value={tag.label}
              maxLength={TAG_MAX_LENGTH}
              forbiddenSymbols={FORBIDDEN_TAG_SYMBOLS}
              isEditing={isEditing}
              isProcessing={isCurrentProcessing}
              onChange={inputValue => (inputValueRef.current = inputValue)}
              onConfirm={() => onActionConfirm('update')}
              onCancel={onCancelEditing}
            />
          </div>
        </div>
        {isEditing ? null : isNewProcessing ? (
          <div className={style['codebook-item-count']}>&nbsp;</div>
        ) : (
          <div className={style['codebook-item-count']}>{tag.amount}</div>
        )}
        {features.oe_tags_management.disabled ? null : (
          <TagSettings
            tagRef={dndItemRef}
            tag={tag}
            opened={isOpened}
            isEditing={isEditing}
            isProcessing={isProcessing}
            isCurrentProcessing={isCurrentProcessing}
            onConfirm={onActionConfirm}
          />
        )}
      </div>

      {error &&
        error.message &&
        'createError' != error.name &&
        tag.codebook == error.codebook &&
        tag.tag == error.tag && <div className={style['component-error']}>{error.message}</div>}
    </>
  )
}

CodingTag.propTypes = {
  tag: PropTypes.object,
  error: PropTypes.object,
  settingsItem: PropTypes.object,
  isEditing: PropTypes.bool,
  isProcessing: PropTypes.bool,
  isCurrentProcessing: PropTypes.bool,
  isNewProcessing: PropTypes.bool,
  onDNDStart: PropTypes.func,
  onDNDEnd: PropTypes.func,
  setDndDroppedTag: PropTypes.func
}

CodingTag.displayName = 'CodingTag'

export default CodingTag
